import { LogicExpressionCondition, LogicExpressionType } from '@doss/prisma/types'
import _ from 'lodash'
import { LogicExpressionInput, LogicExpressionValueInput } from '../antlr/utils/formula.js'
import { resolveFormula } from './formula-resolver.js'
import { ActionData } from './types/action-data.js'
import {
  ComparisonCondition,
  Condition,
  LogicExpression,
  LogicType,
  UnaryCondition,
  UserEnteredLogicInput,
  UserEnteredLogicInputs,
  isComparisonCondition,
  isUnaryCondition,
} from './types/logic.js'
import { TableCellData, getTableCellPrimitiveValue, isTableCellData } from './types/table-cell.js'

/**
 * Maps LogicExpressionCondition to the internal Condition enum used by evaluateLogicExpression.
 */
function mapCondition(condition: LogicExpressionCondition): Condition {
  const conditionMap: Record<LogicExpressionCondition, Condition> = {
    [LogicExpressionCondition.EQUAL]: Condition.EQUAL,
    [LogicExpressionCondition.NOT_EQUAL]: Condition.NOT_EQUAL,
    [LogicExpressionCondition.GREATER_THAN]: Condition.GREATER_THAN,
    [LogicExpressionCondition.GREATER_THAN_OR_EQUAL]: Condition.GREATER_THAN_OR_EQUAL,
    [LogicExpressionCondition.LESS_THAN]: Condition.LESS_THAN,
    [LogicExpressionCondition.LESS_THAN_OR_EQUAL]: Condition.LESS_THAN_OR_EQUAL,
    [LogicExpressionCondition.CONTAINS]: Condition.CONTAINS,
    [LogicExpressionCondition.DOES_NOT_CONTAIN]: Condition.DOES_NOT_CONTAIN,
    [LogicExpressionCondition.IN]: Condition.IN,
    [LogicExpressionCondition.NOT_IN]: Condition.NOT_IN,
    [LogicExpressionCondition.CONTAINS_ANY]: Condition.CONTAINS_ANY,
    [LogicExpressionCondition.CONTAINS_ALL]: Condition.CONTAINS_ALL,
    [LogicExpressionCondition.DOES_NOT_CONTAIN_ANY]: Condition.DOES_NOT_CONTAIN_ANY,
    [LogicExpressionCondition.IS_EMPTY]: Condition.IS_EMPTY,
    [LogicExpressionCondition.IS_NOT_EMPTY]: Condition.IS_NOT_EMPTY,
  }
  return conditionMap[condition]
}

/**
 * Converts a TableCellData or raw value to ActionData.
 * Uses getTableCellPrimitiveValue for TableCellData, otherwise returns the value directly.
 */
function convertToActionData(
  value: TableCellData | Record<string, unknown> | undefined,
): ActionData | null {
  if (!value) {
    return null
  }

  if (isTableCellData(value)) {
    return getTableCellPrimitiveValue(value)
  }

  // For raw values that aren't TableCellData, try to extract primitive
  if ('data' in value && (value as { data: unknown }).data !== undefined) {
    const data = (value as { data: unknown }).data
    if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
      return data
    }
    if (Array.isArray(data)) {
      return data as ActionData
    }
  }

  return null
}

/**
 * Resolves a form input reference to ActionData using the user-entered values.
 */
function resolveFormInputValue(
  formInputId: string,
  userEnteredValues: UserEnteredLogicInputs,
): ActionData | null {
  const userInput: UserEnteredLogicInput | undefined = userEnteredValues[formInputId]
  if (!userInput || !userInput.data) {
    return null
  }

  return getTableCellPrimitiveValue(userInput.data)
}

/**
 * Resolves a single parsed expression value to ActionData.
 */
function resolveValue(
  value: LogicExpressionValueInput,
  userEnteredValues: UserEnteredLogicInputs,
): { resolvedValue?: ActionData | null; resolvedExpression?: LogicExpression } {
  // Nested expression - recursively resolve
  if (value.nestedExpression) {
    return {
      resolvedExpression: resolveLogicExpressionFromParsed(
        value.nestedExpression,
        userEnteredValues,
      ),
    }
  }

  // Handle formula values - recursively resolve
  if (value.formula) {
    const formulaResult = resolveFormula(value.formula, userEnteredValues)
    return { resolvedValue: formulaResult }
  }

  // Form input reference
  if (value.formInputId) {
    return { resolvedValue: resolveFormInputValue(value.formInputId, userEnteredValues) }
  }

  // Table column reference (also look up in userEnteredValues - they may be keyed by columnId)
  if (value.columnId) {
    return { resolvedValue: resolveFormInputValue(value.columnId, userEnteredValues) }
  }

  // Literal value
  if (value.value !== undefined) {
    return { resolvedValue: convertToActionData(value.value ?? undefined) }
  }

  return {}
}

/**
 * Resolves a parsed logic expression (from parseText) into a fully-resolved
 * LogicExpression ready for evaluateLogicExpression().
 *
 * This is a pure function that works with in-memory data, usable by both
 * frontend (form validation) and backend (with pre-loaded data).
 *
 * @param parsedExpression - The parsed expression from parseText
 * @param userEnteredValues - The form values converted to UserEnteredLogicInputs format
 * @returns A fully-resolved LogicExpression ready for evaluation
 */
export function resolveLogicExpressionFromParsed(
  parsedExpression: LogicExpressionInput,
  userEnteredValues: UserEnteredLogicInputs,
): LogicExpression {
  // Sort values by order
  const sortedValues = _.orderBy(parsedExpression.values, (v) => v.order, 'asc')

  // Resolve each value
  const resolved = sortedValues.map((value) => resolveValue(value, userEnteredValues))

  const resolvedValues = resolved
    .map((r) => r.resolvedValue)
    .filter((v): v is ActionData | null => v !== undefined)

  const resolvedExpressions = resolved
    .map((r) => r.resolvedExpression)
    .filter((e): e is LogicExpression => e !== undefined)

  // Build the appropriate LogicExpression based on type
  switch (parsedExpression.type) {
    case LogicExpressionType.AND: {
      return {
        type: LogicType.AND,
        expressions: resolvedExpressions,
      }
    }

    case LogicExpressionType.OR: {
      return {
        type: LogicType.OR,
        expressions: resolvedExpressions,
      }
    }

    case LogicExpressionType.NOT: {
      if (resolvedExpressions.length !== 1) {
        throw new Error(
          `NOT expression must have exactly one nested expression. Got: ${resolvedExpressions.length}`,
        )
      }
      return {
        type: LogicType.NOT,
        expression: resolvedExpressions[0],
      }
    }

    case LogicExpressionType.BOOLEAN: {
      if (resolvedValues.length !== 1 || typeof resolvedValues[0] !== 'boolean') {
        throw new Error(
          `BOOLEAN expression must have a single boolean value. Got: ${JSON.stringify(resolvedValues)}`,
        )
      }
      return {
        type: LogicType.BOOLEAN,
        value: resolvedValues[0],
      }
    }

    case LogicExpressionType.UNARY: {
      if (!parsedExpression.condition) {
        throw new Error('UNARY expression must have a condition')
      }
      const condition = mapCondition(parsedExpression.condition)
      if (!isUnaryCondition(condition)) {
        throw new Error(`Invalid UNARY condition: ${parsedExpression.condition}`)
      }
      return {
        type: LogicType.UNARY,
        condition: condition as UnaryCondition,
        value: resolvedValues[0] ?? null,
      }
    }

    case LogicExpressionType.COMPARISON: {
      if (!parsedExpression.condition) {
        throw new Error('COMPARISON expression must have a condition')
      }

      const left = resolvedValues[0] ?? null
      const right = resolvedValues[1] ?? null
      const condition = mapCondition(parsedExpression.condition)

      if (!isComparisonCondition(condition)) {
        throw new Error(`Invalid COMPARISON condition: ${parsedExpression.condition}`)
      }

      return {
        type: LogicType.COMPARISON,
        condition: condition as ComparisonCondition,
        left,
        right,
      }
    }

    default:
      throw new Error(`Unknown expression type: ${parsedExpression.type}`)
  }
}
